import React, { useEffect, useState } from 'react';

import DeleteRounded from '@mui/icons-material/DeleteRounded';
import HowToRegRounded from '@mui/icons-material/HowToRegRounded';
import { Skeleton, Stack } from '@mui/material';
import { useNavigate } from 'react-router';

import Button from '../../../../components/Button';
import ContentCard from '../../../../components/ContentCard';
import Modal from '../../../../components/Modal';
import { useConvertLeadMutation, useDeleteLeadMutation } from '../../../../services/leads';
import { Lead } from '../../../../services/leads/types';
import { LeadType } from '../../../../types/common';
import useError from '../../../../utils/react/hooks/useError';

interface ActionsCardProps {
  lead?: Lead,
}

const ActionsCard = ({ lead } : ActionsCardProps) => {
  const navigate = useNavigate();

  /**
   * Lead Conversion
   */
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [newStudentId, setNewStudentId] = useState<string>();
  const [convertLead, {
    isLoading: isConvertLeadLoading,
    isSuccess: isConvertLeadSuccess,
    isError: isConvertLeadError,
    error: convertLeadError,
  }] = useConvertLeadMutation();

  useError(isConvertLeadError, convertLeadError);

  // Once the lead conversion is completed, redirect to the student page
  useEffect(() => {
    if (isConvertLeadSuccess && newStudentId) {
      navigate(`/admin/student/${newStudentId}`);
    }
  }, [isConvertLeadSuccess, newStudentId]);

  const convertLeadToStudent = async () => {
    if (lead) {
      const result = await convertLead({ type: lead.type, leadId: lead.leadId }).unwrap();
      setNewStudentId(result?.data?.studentId);
    }
  };

  /**
   * Lead Deletion
   */
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLead, {
    isLoading: isDeleteLeadLoading,
    isSuccess: isDeleteLeadSuccess,
    isError: isDeleteLeadError,
    error: deleteLeadError,
  }] = useDeleteLeadMutation();

  useError(isDeleteLeadError, deleteLeadError);

  // Once the lead deletion is successful, redirect to the leads page
  useEffect(() => {
    if (isDeleteLeadSuccess) {
      navigate('/admin/leads');
    }
  }, [isDeleteLeadSuccess]);

  if (!lead) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Actions">
      <Stack direction="column" spacing={1}>
        <div>
          <Button
            color="blue"
            disabled={lead.type !== LeadType.Enrolment}
            variant="contained"
            onClick={() => setConvertModalOpen(true)}
            startIcon={(<HowToRegRounded />)}
          >
            Convert to Student
          </Button>
        </div>
        <div>
          <Button
            color="error"
            onClick={() => setDeleteModalOpen(true)}
            startIcon={(<DeleteRounded />)}
          >
            Delete Permanently
          </Button>
        </div>
      </Stack>
      <Modal
        open={convertModalOpen}
        handleClose={() => setConvertModalOpen(false)}
        title="Are you sure?"
        description="This will transfer all lead details / notes and can not be undone."
        buttons={[
          {
            label: 'Convert',
            onClick: convertLeadToStudent,
            color: 'blue',
            loading: isConvertLeadLoading,
          },
        ]}
      />
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permamently delete this lead and can not be undone."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteLead({ type: lead.type, leadId: lead.leadId }),
            color: 'error',
            loading: isDeleteLeadLoading,
          },
        ]}
      />
    </ContentCard>
  );
};

export default ActionsCard;
